import { useState } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { BiErrorCircle } from "react-icons/bi";
import { MdOutlineMovieCreation } from "react-icons/md";
import Lottie from "lottie-react";
import { Button } from "MVVM/Components/Button";
import { useUser } from "MVVM/Hooks/useUser";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { TMoment } from "MVVM/Views/MatchHistory/@types";

import Remixing from "../../../../../../../animations/remixing.json";
import { ClipThumbnail } from "../ClipThumbnail";
import { ReactComponent as RemixIcon } from "../../../../../../../assets/images/badges/remix-icon.svg";
import { MomentClipViewModel } from "./ViewModel";
import {
  ProcessingButton,
  TransitTimeWrapper,
} from "MVVM/Views/MatchHistory/styledComponents";
import { TransitTime } from "MVVM/Components/TransitTime";
import { usePortal } from "MVVM/Hooks/usePortal";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { analytics } from "MVVM/Analytics";

const MomentClip = ({
  moment,
  outdated,
  clipTags,
}: {
  moment: TMoment;
  outdated?: boolean;
  clipTags?: { key: string; value: string }[];
}) => {
  const { clip } = moment;
  const { allstarUser } = useUser();
  const history = useHistory();
  const isPruned = clip.status === "PRUNED";
  const [processing, setProcessing] = useState(false);
  const { createPrunedClip } = MomentClipViewModel();
  const {
    portalOpen: quickEditPortalOpen,
    setPortalOpen: setQuickEditPortalOpen,
    Portal: QuickEditPortal,
  } = usePortal(
    <QuickEdit
      momentId={moment.id}
      closeModal={() => setQuickEditPortalOpen(false)}
    />,
    "root",
  );
  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <TransitTimeWrapper>
      <TransitTime user={allstarUser?.user?._id} />
    </TransitTimeWrapper>,
  );
  const handleClick = async () => {
    setProcessing(true);
    createPrunedClip({
      variables: { clipIdentifier: clip.shareId },
    });
  };

  return (
    <>
      {portalOpen && Portal}
      {quickEditPortalOpen && QuickEditPortal}
      {isPruned ? (
        <Flex
          flexDirection={["column", "column", "row"]}
          alignItems={["center", "center", "start"]}
          style={{ gap: 12 }}
        >
          <PrunedThumbnail
            style={{
              backgroundImage: `url(${clip?.thumbnailUrl})`,
            }}
          >
            <DarkOverlay />
            <StyledErrorIcon />
          </PrunedThumbnail>
          <Flex flexDirection="column" flexGrow={1} style={{ gap: 6 }}>
            <Text variant="text.labelSmall" color="ice">
              This Clip Was Archived
            </Text>
            <PrunedText variant="text.labelXSmall" color="chalk">
              Sorry, this clip was archived because no one was watching it. You
              can create it again at any time.
            </PrunedText>
          </Flex>
          {!outdated && (
            <Flex flexShrink={0} alignItems="center">
              {processing ? (
                <StyledProcessingButton
                  flex={true}
                  size="tiny"
                  flexShrink={0}
                  maxWidth={150}
                  onClick={() => setPortalOpen(true)}
                >
                  Clip Pending
                  <Lottie
                    animationData={Remixing}
                    loop={true}
                    style={{ height: 36, width: 36, minWidth: 36 }}
                  />
                </StyledProcessingButton>
              ) : (
                <ClipButton
                  size="tiny"
                  classification="roundedFilled"
                  flex={true}
                  onClick={async () => handleClick()}
                  onClickCapture={() => {}}
                  maxWidth={100}
                >
                  <StyledMdOutlineMovieCreation />
                  Restore
                </ClipButton>
              )}
            </Flex>
          )}
        </Flex>
      ) : (
        <Flex alignItems="center" style={{ gap: 10 }}>
          <ClipThumbnail clip={clip} />
          <Flex
            flexGrow={1}
            flexDirection="column"
            style={{ gap: 2, minWidth: 0 }}
          >
            <ClipInfoRow>
              <ClipTitle>{moment.title} has been clipped</ClipTitle>
              {clipTags && clipTags.length > 0 && (
                <ClipBadges tags={clipTags} size="sm" maxVisible={1} />
              )}
            </ClipInfoRow>
            <Text fontSize={14} fontWeight={500} color="chalk">
              Remix it to create something new
            </Text>
          </Flex>
          {!outdated && (
            <RemixButton
              onClick={() => {
                setQuickEditPortalOpen(true);
                analytics.track("Match History League - Remix Button Hit", {
                  userGroup: allstarUser?.user?.userGroup?.type,
                });
              }}
            >
              <StyledRemixIcon />
              Remix
            </RemixButton>
          )}
        </Flex>
      )}
    </>
  );
};

const StyledProcessingButton = styled(ProcessingButton)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
`;

const StyledMdOutlineMovieCreation = styled(MdOutlineMovieCreation)`
  color: ${({ theme }) => theme.colors.ice};
  height: 20px;
  margin-right: 6px;
  min-width: 16px;
  width: 20px;
`;

const ClipButton = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes[1]};
  font-weight: 700;
  padding: 16px;

  :disabled {
    cursor: not-allowed;
  }

  :disabled svg {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;
const PrunedText = styled(Text)`
  max-width: 250px;
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    max-width: 100%;
  }
`;

const DarkOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  z-index: 1;
`;
const StyledErrorIcon = styled(BiErrorCircle)`
  color: ${({ theme }) => theme.colors.error};
  height: 20px;
  width: 20px;
  z-index: 2;
`;

const RemixButton = styled.button`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.midnight};
  background-image: linear-gradient(
    225deg,
    rgba(249, 83, 112, 0.4) 0%,
    rgba(114, 67, 154, 0.4) 100%
  );
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  gap: 4px;
  padding: 8px 12px;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.colors.darkerNed};
  }
`;

const StyledRemixIcon = styled(RemixIcon)`
  height: 16px;
  width: 16px;
`;
const PrunedThumbnail = styled(Flex)`
  align-items: center;
  background-size: cover;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  height: 36px;
  justify-content: center;
  position: relative;
  width: 64px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    flex-grow: 1;
    height: inherit;
    width: 100%;
    aspect-ratio: 16/9;
  }
`;

const ClipInfoRow = styled(Flex)`
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
`;

const ClipTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  flex-shrink: 1;
  font-size: 15px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export { MomentClip };
